import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import "./site.css";

/* ---------- shared helpers ---------- */

export function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const targets = root.querySelectorAll<HTMLElement>("[data-reveal]");
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      targets.forEach(el => el.classList.add("hs-in"));
      return;
    }
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) { entry.target.classList.add("hs-in"); observer.unobserve(entry.target); }
    }), { threshold: .15, rootMargin: "0px 0px -8%" });
    targets.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return ref;
}

export function Keycap({ children, hot = false }: { children: ReactNode; hot?: boolean }) {
  return <kbd className={`hs-key ${hot ? "hot" : ""}`}>{children}</kbd>;
}

/* ---------- discipline data ---------- */

export type DisciplineId = "editing" | "gaming" | "design" | "development";

type Moment = { at: number; time: string; tag: string; observation: string; evidence: string[]; next: string };

export const disciplineData: Record<DisciplineId, { label: string; app: string; length: string; moments: Moment[] }> = {
  editing: {
    label: "Editing", app: "Timeline session", length: "42:18",
    moments: [
      { at: 18, time: "07:36", tag: "Repeated action", observation: "The same trim was nudged 6 times before a single playback.", evidence: ["6× trim edits in 41s", "0 playbacks between edits", "Marker: “feels off”"], next: "Play the cut once before adjusting. Let one viewing decide the next trim." },
      { at: 47, time: "19:52", tag: "Tool path", observation: "Clips were moved by dragging across 3 tracks instead of a ripple delete.", evidence: ["14 drag operations", "Menu → Edit → Ripple used once", "Avg. 5.2s per move"], next: "Try the ripple-delete shortcut for this pattern. Compare time on the next session." },
      { at: 78, time: "33:04", tag: "Pacing", observation: "Cut length shortened steadily through the last act, then jumped back.", evidence: ["Avg. clip 3.1s → 1.4s", "Single 8.2s outlier", "Your note: “rushed?”"], next: "Watch the final act at full speed. Decide whether the long shot is intentional." },
    ],
  },
  gaming: {
    label: "Gaming", app: "Ranked match", length: "31:47",
    moments: [
      { at: 22, time: "06:58", tag: "Decision window", observation: "You peeked the second angle 0.4s before your utility landed.", evidence: ["Key: W held 380ms early", "Utility thrown at 06:57.6", "Marker: “why did I die”"], next: "On the next similar entry, wait for the flash pop before you commit." },
      { at: 55, time: "17:20", tag: "Repeated pattern", observation: "Reload started immediately after each of 5 early-round kills.", evidence: ["R pressed ≤0.3s after kill ×5", "2 re-peeks during reload", "Round losses: 2 of 5"], next: "Check the trade angle first. Reload only once your crosshair is safe." },
      { at: 84, time: "26:41", tag: "Input timing", observation: "Counter-strafe timing drifted late as the match went on.", evidence: ["Stop-to-shot 92ms → 141ms", "Across 38 duels", "Late-match only"], next: "Warm the stop-shot rhythm before round 20 and see whether the drift holds." },
    ],
  },
  design: {
    label: "Design", app: "Interface file", length: "58:02",
    moments: [
      { at: 16, time: "09:14", tag: "Revision path", observation: "The header layout was rebuilt from scratch twice in 11 minutes.", evidence: ["3 frame versions", "Undo stack cleared ×2", "Final ≈ version 1"], next: "Duplicate the frame before exploring. Compare side by side instead of rebuilding." },
      { at: 51, time: "29:37", tag: "Repeated action", observation: "Spacing was corrected by hand on 4 instances of one component.", evidence: ["4 identical edits", "Same 8 → 12px change", "No shared property"], next: "Move the value into the component and see whether any instance should differ." },
      { at: 80, time: "46:25", tag: "Point of friction", observation: "The color picker was reopened 9 times while matching one tone.", evidence: ["9 picker sessions", "Hex copied manually ×4", "No style saved"], next: "Save the tone as a style the first time you settle on it." },
    ],
  },
  development: {
    label: "Development", app: "Debugging session", length: "1:12:40",
    moments: [
      { at: 20, time: "14:31", tag: "Debugging sequence", observation: "Two changes were made before the failing test was run again.", evidence: ["2 files edited", "Test run gap: 6m 12s", "Marker: “fixed?”"], next: "Change one assumption, rerun the same case, then decide on the next edit." },
      { at: 49, time: "35:08", tag: "Repeated detour", observation: "The same doc page was opened from search 5 times.", evidence: ["5 visits, avg. 38s", "Same section each time", "Not bookmarked"], next: "Pin the reference or copy the snippet into your notes during the session." },
      { at: 81, time: "58:52", tag: "Navigation", observation: "Files were found by scrolling the tree instead of fuzzy-search.", evidence: ["23 tree expansions", "Ctrl+P used twice", "Avg. 9.4s to open"], next: "Use quick-open for the next session and compare time-to-file." },
    ],
  },
};

export const disciplineOrder: DisciplineId[] = ["editing", "gaming", "design", "development"];

/* ---------- scenes (code-rendered footage) ---------- */

export function Scene({ kind, moment = 0 }: { kind: DisciplineId; moment?: number }) {
  if (kind === "editing") return <div className="hs-scene hs-scene-editing" data-moment={moment}>
    <div className="se-preview"><div className="se-frame" /><span className="se-tc">00:07:36:12</span></div>
    <div className="se-tracks">{[0, 1, 2].map(track => <div key={track} className="se-track">{Array.from({ length: 7 }, (_, i) => <i key={i} style={{ "--w": `${[14, 9, 18, 7, 12, 16, 10][(i + track * 2) % 7]}%` } as CSSProperties} />)}</div>)}<b className="se-head" /></div>
  </div>;
  if (kind === "gaming") return <div className="hs-scene hs-scene-gaming" data-moment={moment}>
    <div className="sg-map"><i className="sg-wall a" /><i className="sg-wall b" /><i className="sg-wall c" /><i className="sg-cone" /><b className="sg-you" /><b className="sg-enemy" /><b className="sg-enemy two" /><span className="sg-util" /></div>
    <div className="sg-hud"><span>R 14</span><span>HP 100</span><span>24 / 90</span></div>
    <div className="sg-cross" />
  </div>;
  if (kind === "design") return <div className="hs-scene hs-scene-design" data-moment={moment}>
    <div className="sd-layers">{Array.from({ length: 8 }, (_, i) => <i key={i} />)}</div>
    <div className="sd-canvas"><div className="sd-board"><i className="sd-head" /><div className="sd-cards">{[0, 1, 2, 3].map(i => <i key={i} />)}</div><i className="sd-cta" /></div><span className="sd-select" /></div>
    <div className="sd-props">{Array.from({ length: 6 }, (_, i) => <i key={i} />)}</div>
  </div>;
  return <div className="hs-scene hs-scene-dev" data-moment={moment}>
    <div className="sv-code">{[62, 40, 78, 30, 55, 70, 24, 48, 66, 36, 58].map((w, i) => <i key={i} style={{ "--w": `${w}%`, "--d": `${(i % 4) * 14}px` } as CSSProperties} />)}</div>
    <div className="sv-term"><span className="fail">✕ parser › handles nested blocks</span><span className="pass">✓ parser › empty input</span><span className="dim">Tests: 1 failed, 41 passed</span></div>
  </div>;
}

/* ---------- interactive review demo ---------- */

export function ReviewDemo({ initial = "editing", locked = false }: { initial?: DisciplineId; locked?: boolean }) {
  const [kind, setKind] = useState<DisciplineId>(initial);
  const [index, setIndex] = useState(0);
  const [auto, setAuto] = useState(true);
  const root = useRef<HTMLDivElement>(null);
  const data = disciplineData[kind];
  const moment = data.moments[index]!;

  useEffect(() => {
    if (!auto || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let visible = false;
    const observer = new IntersectionObserver(([entry]) => { visible = !!entry?.isIntersecting; }, { threshold: .4 });
    if (root.current) observer.observe(root.current);
    const timer = window.setInterval(() => { if (visible) setIndex(i => (i + 1) % data.moments.length); }, 5200);
    return () => { observer.disconnect(); window.clearInterval(timer); };
  }, [auto, data.moments.length]);

  const choose = (i: number) => { setAuto(false); setIndex(i); };

  return <div className="hs-demo" ref={root}>
    {!locked && <div className="hs-tabs" role="group" aria-label="Choose a discipline">{disciplineOrder.map(id => <button type="button" key={id} aria-pressed={kind === id} onClick={() => { setKind(id); setIndex(0); setAuto(true); }}>{disciplineData[id].label}</button>)}</div>}
    <div className="hs-window">
      <div className="hs-window-bar"><span className="hs-dots" aria-hidden="true"><i /><i /><i /></span><span className="hs-window-title">Review · {data.app}</span><span className="hs-chip">Concept preview</span></div>
      <div className="hs-demo-body">
        <div className="hs-demo-stage">
          <div className="hs-demo-screen"><Scene kind={kind} moment={index} /><span className="hs-focus-ring" style={{ "--x": `${[30, 62, 46][index]}%`, "--y": `${[38, 52, 30][index]}%` } as CSSProperties} aria-hidden="true" /></div>
          <div className="hs-timeline" role="group" aria-label="Session moments">
            <div className="hs-lanes" aria-hidden="true">
              <div className="hs-lane"><em>Frames</em><div className="hs-frames">{Array.from({ length: 24 }, (_, i) => <i key={i} style={{ opacity: .35 + ((i * 37) % 60) / 100 }} />)}</div></div>
              <div className="hs-lane"><em>Input</em><div className="hs-ticks">{Array.from({ length: 64 }, (_, i) => <i key={i} style={{ left: `${(i * 1.57 + (i % 5) * .9) % 100}%`, height: `${30 + ((i * 53) % 70)}%` }} />)}</div></div>
              <div className="hs-lane"><em>Markers</em><div className="hs-markers">{data.moments.map(m => <i key={m.time} style={{ left: `${m.at}%` }} />)}</div></div>
            </div>
            <div className="hs-playhead" style={{ left: `calc(64px + (100% - 64px) * ${moment.at / 100})` }} aria-hidden="true" />
            <div className="hs-moment-buttons">{data.moments.map((m, i) => <button type="button" key={m.time} aria-pressed={i === index} style={{ left: `calc(64px + (100% - 64px) * ${m.at / 100})` }} onClick={() => choose(i)} aria-label={`Moment at ${m.time}: ${m.tag}`}><span>{m.time}</span></button>)}</div>
          </div>
        </div>
        <aside className="hs-finding" aria-live="polite" key={`${kind}-${index}`}>
          <div className="hs-finding-head"><span className="hs-tag">{moment.tag}</span><span className="hs-mono">{moment.time} / {data.length}</span></div>
          <h3>{moment.observation}</h3>
          <div className="hs-evidence"><span className="hs-small">Evidence</span><ul>{moment.evidence.map(e => <li key={e}>{e}</li>)}</ul></div>
          <div className="hs-next"><span className="hs-small">Next attempt</span><p>{moment.next}</p></div>
          <div className="hs-finding-nav"><button type="button" onClick={() => choose((index + data.moments.length - 1) % data.moments.length)} aria-label="Previous moment">←</button><span>{index + 1} of {data.moments.length}</span><button type="button" onClick={() => choose((index + 1) % data.moments.length)} aria-label="Next moment">→</button></div>
        </aside>
      </div>
    </div>
    <p className="hs-footnote">Authored example of the review Hindset is being built to produce. Not generated from a real recording.</p>
  </div>;
}

/* ---------- capture → connect → review scroll story ---------- */

const loopSteps = [
  { id: "capture", n: "01", title: "Work like you always do.", body: "One shortcut starts it. Nothing pops up." },
  { id: "connect", n: "02", title: "Every signal, on one clock.", body: "Screen, keys and the moments you mark, in sync." },
  { id: "review", n: "03", title: "Come back to what mattered.", body: "A handful of moments, and one thing to try next time." },
] as const;

function LoopVisual({ step }: { step: number }) {
  return <div className={`hs-loop-visual step-${step}`} aria-hidden="true">
    <div className="lv-screen">
      <div className="lv-app"><div className="lv-side">{Array.from({ length: 6 }, (_, i) => <i key={i} />)}</div><div className="lv-main"><Scene kind="editing" /></div></div>
      <div className="lv-rec"><b />REC <span className="hs-mono">00:14:22</span></div>
      <div className="lv-keys"><Keycap>Ctrl</Keycap><Keycap>K</Keycap><Keycap hot>Q</Keycap><Keycap>W</Keycap><Keycap>Space</Keycap></div>
      <div className="lv-flag">Marked moment</div>
    </div>
    <div className="lv-timeline">
      {["Frames", "Input", "Markers", "App"].map((lane, li) => <div className="lv-lane" key={lane}><em>{lane}</em><div>{li === 0 && Array.from({ length: 18 }, (_, i) => <i key={i} className="lv-frame" />)}{li === 1 && Array.from({ length: 48 }, (_, i) => <i key={i} className="lv-tick" style={{ left: `${(i * 2.07 + (i % 3)) % 100}%`, height: `${30 + ((i * 41) % 70)}%` }} />)}{li === 2 && [22, 58, 81].map(x => <i key={x} className="lv-mark" style={{ left: `${x}%` }} />)}{li === 3 && [["Timeline", 0, 44], ["Effects", 44, 18], ["Timeline", 62, 38]].map(([n, l, w]) => <span key={`${n}${l}`} className="lv-ctx" style={{ left: `${l}%`, width: `${w}%` }}>{n}</span>)}</div></div>)}
      <b className="lv-head" />
    </div>
    <div className="lv-card"><div className="hs-finding-head"><span className="hs-tag">Repeated action</span><span className="hs-mono">07:36</span></div><strong>The same trim was nudged 6 times before a single playback.</strong><p><span className="hs-small">Next attempt</span>Play the cut once before adjusting.</p></div>
  </div>;
}

export function LoopStory() {
  const [step, setStep] = useState(0);
  const refs = useRef<(HTMLElement | null)[]>([]);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) setStep(Number((entry.target as HTMLElement).dataset.step));
    }), { rootMargin: "-45% 0px -45% 0px" });
    refs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return <div className="hs-loop">
    <div className="hs-loop-steps">{loopSteps.map((s, i) => <article key={s.id} data-step={i} ref={el => { refs.current[i] = el; }} className={step === i ? "active" : ""}>
      <span className="hs-mono hs-step-n">{s.n}</span><h3>{s.title}</h3><p>{s.body}</p>
      <div className="hs-loop-inline"><LoopVisual step={i} /></div>
    </article>)}</div>
    <div className="hs-loop-sticky"><LoopVisual step={step} /><div className="hs-loop-progress">{loopSteps.map((s, i) => <span key={s.id} className={i <= step ? "on" : ""}>{s.id}</span>)}</div></div>
  </div>;
}

/* ---------- build status ---------- */

export const buildStatus = [
  { state: "Working in development", tone: "live", items: ["Focused-monitor recording at 60 FPS", "Global record and mark-moment hotkeys", "Recording-scoped physical key timing", "Embedded playback that seeks to events and notes", "Side-by-side comparison of two sessions", "Clip export, goals, notes and progress history", "Checksummed local backup and restore"] },
  { state: "Building next", tone: "next", items: ["First pattern detectors for one workflow", "Evidence-linked findings in review", "Windows and macOS capture", "Signed installers and updates"] },
  { state: "On the horizon", tone: "later", items: ["Specialist packs per game and application", "Adaptive feedback styles", "Opt-in sharing with a coach or team", "Long-term skill trends"] },
] as const;

export function BuildBoard() {
  return <div className="hs-board">{buildStatus.map(col => <div key={col.state} className={`hs-board-col ${col.tone}`} data-reveal>
    <div className="hs-board-head"><i aria-hidden="true" /><span>{col.state}</span><em>{col.items.length}</em></div>
    <ul>{col.items.map(item => <li key={item}>{item}</li>)}</ul>
  </div>)}</div>;
}

/* ---------- discipline cards ---------- */

export const disciplineCards: { id: DisciplineId; href: string; title: string; line: string }[] = [
  { id: "gaming", href: "/gaming", title: "Gaming", line: "The habits that cost you rounds." },
  { id: "editing", href: "/editing", title: "Editing", line: "The minutes hiding in your timeline." },
  { id: "design", href: "/design", title: "Design", line: "Where the tool got in the way." },
  { id: "development", href: "/development", title: "Development", line: "The detours behind the fix." },
];

export function DisciplineGrid({ exclude }: { exclude?: DisciplineId }) {
  return <div className="hs-disc-grid">{disciplineCards.filter(c => c.id !== exclude).map(card => <a key={card.id} href={card.href} className={`hs-disc-card ${card.id}`} data-reveal>
    <div className="hs-disc-scene" aria-hidden="true"><Scene kind={card.id} /></div>
    <div className="hs-disc-copy"><h3>{card.title}</h3><p>{card.line}</p><span className="hs-arrow">Explore <b aria-hidden="true">→</b></span></div>
  </a>)}</div>;
}

/* ---------- misc sections ---------- */

const voices = {
  Direct: "You committed before the opening appeared. Wait one beat longer.",
  Socratic: "What changed between noticing the opening and acting on it?",
  Strategic: "Early commitment keeps narrowing your options. Preserve flexibility for one more beat.",
};

export function VoiceSwitch() {
  const [voice, setVoice] = useState<keyof typeof voices>("Direct");
  return <div className="hs-voice" data-reveal>
    <div className="hs-tabs" role="group" aria-label="Feedback style">{(Object.keys(voices) as (keyof typeof voices)[]).map(v => <button type="button" key={v} aria-pressed={voice === v} onClick={() => setVoice(v)}>{v}</button>)}</div>
    <blockquote key={voice}>“{voices[voice]}”</blockquote>
    <p className="hs-footnote">Same moment, same evidence — delivered the way you think. Planned feature.</p>
  </div>;
}

export function Faq({ items }: { items: [string, string][] }) {
  const [open, setOpen] = useState<number | null>(0);
  return <div className="hs-faq">{items.map(([q, a], i) => <div key={q} className={`hs-faq-item ${open === i ? "open" : ""}`}>
    <button type="button" aria-expanded={open === i} onClick={() => setOpen(open === i ? null : i)}>{q}<span aria-hidden="true">+</span></button>
    <div className="hs-faq-a"><p>{a}</p></div>
  </div>)}</div>;
}

export function Cta({ title, body }: { title: ReactNode; body: string }) {
  return <section className="hs-cta" data-reveal>
    <div className="hs-cta-glow" aria-hidden="true" />
    <h2>{title}</h2><p>{body}</p>
    <div className="hs-actions"><a className="hs-btn primary" href="/early-access">Request early access <b aria-hidden="true">→</b></a><a className="hs-btn ghost" href="/product">See the product</a></div>
    <span className="hs-small">Free · No account · Stays on your machine</span>
  </section>;
}
